import React, { useState, useMemo, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import { useNotification } from '../../../components/ui/NotificationProvider';

const ProductPreviewTable = ({ 
  products = [], 
  onProductUpdate, 
  onProductDelete,
  onBulkSelect, 
  selectedProducts = [],
  onValidationUpdate,
  selectedColumns = {}, // Add selectedColumns prop
  onErrorAnalysis, // Add error analysis callback
  allColumns = [] // Add allColumns prop for reference
}) => {
  const { showConfirmation } = useNotification();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showErrorAnalysis, setShowErrorAnalysis] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showAllColumns, setShowAllColumns] = useState(false);

  // Filtros tipo Excel
  const [excelFilters, setExcelFilters] = useState({});
  const [filterColumn, setFilterColumn] = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const itemsPerPage = 50; // Increased from 10 to 50 to handle more products efficiently

  // Definición estricta de las 28 columnas del esquema requerido
  const visibleColumns = useMemo(() => [
    { key: 'asin', label: 'asin' },
    { key: 'originalAsin', label: 'originalAsin' },
    { key: 'título', label: 'título' },
    { key: 'precio', label: 'precio' },
    { key: 'marca', label: 'marca' },
    { key: 'descripción', label: 'descripción' },
    { key: 'Descripción general del producto', label: 'Descripción general del producto' },
    { key: 'características', label: 'características' },
    { key: 'atributos', label: 'atributos' },
    { key: 'atributos de variante', label: 'atributos de variante' },
    { key: 'Detalles de la variante', label: 'Detalles de la variante' },
    { key: 'variantesAsins', label: 'variantesAsins' },
    { key: 'Imágenes de alta resolución', label: 'Imágenes de alta resolución' },
    { key: 'URL de producto no normalizada', label: 'URL de producto no normalizada' },
    { key: 'URL', label: 'URL' },
    { key: 'texto de ubicación', label: 'texto de ubicación' },
    { key: 'Reseñas de la página del producto', label: 'Reseñas de la página del producto' },
    { key: 'Información importante', label: 'Información importante' }
  ], []);

  // Columnas dinámicas: cuando el archivo trae campos extra, permitir verlos.
  // Tomamos una muestra para evitar costos altos en datasets grandes.
  const allDetectedColumns = useMemo(() => {
    const baseKeys = new Set((visibleColumns || []).map(c => c.key));
    const keys = new Set();
    const sample = Array.isArray(products) ? products.slice(0, 200) : [];
    for (const p of sample) {
      if (!p || typeof p !== 'object') continue;
      Object.keys(p).forEach(k => {
        if (!k) return;
        if (k === 'id') return;
        keys.add(k);
      });
    }
    const extras = Array.from(keys)
      .filter(k => !baseKeys.has(k))
      .sort((a, b) => String(a).localeCompare(String(b)));
    return [
      ...(visibleColumns || []),
      ...extras.map(k => ({ key: k, label: k }))
    ];
  }, [products, visibleColumns]);

  // Auto-mostrar columnas extra cuando el archivo trae atributos adicionales.
  // Evita activar esto si el dataset trae demasiadas columnas (tabla inmanejable).
  useEffect(() => {
    const extraCount = Math.max(0, (allDetectedColumns?.length || 0) - (visibleColumns?.length || 0));
    const MAX_AUTO_COLUMNS = 40;
    if (extraCount > 0 && (allDetectedColumns?.length || 0) <= MAX_AUTO_COLUMNS) {
      setShowAllColumns(true);
    } else {
      setShowAllColumns(false);
    }
  }, [products, allDetectedColumns?.length, visibleColumns?.length]);

  const columnsToRender = showAllColumns ? allDetectedColumns : visibleColumns;

  const normalizeExcelValue = (value) => {
    if (value === null || value === undefined || value === '') {
      return '__EMPTY__';
    }

    if (Array.isArray(value)) {
      return value.map(item => {
        if (typeof item === 'object') {
          try {
            return JSON.stringify(item);
          } catch {
            return String(item);
          }
        }
        return String(item);
      }).join(' | ');
    }

    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    }

    return String(value);
  };

  const displayExcelValue = (value) =>
    value === '__EMPTY__' ? '(Vacías)' : value;

  const activeFilterCount = Object.keys(excelFilters).length;

  const currentColumnValues = useMemo(() => {
    if (!filterColumn) return [];

    const values = new Set();

    products.forEach(product => {
      values.add(
        normalizeExcelValue(product?.[filterColumn])
      );
    });

    return Array.from(values).sort((a, b) => {
      if (a === '__EMPTY__') return 1;
      if (b === '__EMPTY__') return -1;

      const aNum = Number(a);
      const bNum = Number(b);

      if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
        return aNum - bNum;
      }

      return String(a).localeCompare(String(b), 'es', {
        numeric: true,
        sensitivity: 'base'
      });
    });
  }, [products, filterColumn]);

  const visibleFilterValues = useMemo(() => {
    const term = String(filterSearch || '')
      .trim()
      .toLowerCase();

    if (!term) return currentColumnValues;

    return currentColumnValues.filter(value =>
      displayExcelValue(value)
        .toLowerCase()
        .includes(term)
    );
  }, [currentColumnValues, filterSearch]);

  const selectedColumnValues =
    filterColumn
      ? excelFilters?.[filterColumn]?.values
      : null;

  const toggleExcelValue = (value) => {
    if (!filterColumn) return;

    setExcelFilters(prev => {
      const allValues = currentColumnValues;

      const current =
        prev?.[filterColumn]?.values ?? allValues;

      const nextValues = current.includes(value)
        ? current.filter(item => item !== value)
        : [...current, value];

      const next = { ...prev };

      const allSelected =
        nextValues.length === allValues.length &&
        allValues.every(item => nextValues.includes(item));

      if (allSelected) {
        delete next[filterColumn];
      } else {
        next[filterColumn] = {
          values: nextValues
        };
      }

      return next;
    });
  };

  const selectAllExcelValues = () => {
    if (!filterColumn) return;

    setExcelFilters(prev => {
      const next = { ...prev };
      delete next[filterColumn];
      return next;
    });
  };

  const clearAllExcelValues = () => {
    if (!filterColumn) return;

    setExcelFilters(prev => ({
      ...prev,
      [filterColumn]: {
        values: []
      }
    }));
  };

  const clearColumnExcelFilter = (columnKey) => {
    setExcelFilters(prev => {
      const next = { ...prev };
      delete next[columnKey];
      return next;
    });
  };

  const clearAllExcelFilters = () => {
    setExcelFilters({});
    setFilterSearch('');
  };

  // Mover arriba: función utilizada por filtros y tabla
  const getValidationStatus = () => ({
    isValid: true,
    hasWarnings: false,
    errors: [],
    warnings: [],
    totalIssues: 0
  });

  const filteredProducts = useMemo(() => {
    const term = String(searchTerm || '').toLowerCase();

    return products
      .filter(product => {
        if (!term) return true;

        return Object.values(product || {}).some(value => {
          if (
            value === null ||
            value === undefined ||
            typeof value === 'object'
          ) {
            return false;
          }

          return String(value)
            .toLowerCase()
            .includes(term);
        });
      })
      .filter(product => {
        return Object.entries(excelFilters).every(
          ([columnKey, config]) => {
            const value = normalizeExcelValue(
              product?.[columnKey]
            );

            return (config?.values || []).includes(value);
          }
        );
      });
  }, [products, searchTerm, excelFilters]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, excelFilters]);

  const sortedProducts = useMemo(() => {
    if (!sortConfig?.key) return filteredProducts;

    return [...filteredProducts]?.sort((a, b) => {
      const aValue = a?.[sortConfig?.key];
      const bValue = b?.[sortConfig?.key];

      if (aValue < bValue) {
        return sortConfig?.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig?.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredProducts, sortConfig]);

  const paginatedProducts = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedProducts?.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedProducts, currentPage]);

  const totalPages = Math.ceil(sortedProducts?.length / itemsPerPage);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Fixed: Select ALL products across all pages, not just current page
  const handleSelectAll = (checked) => {
    if (checked) {
      // Select ALL products, not just paginated ones
      onBulkSelect?.(sortedProducts?.map(p => p?.id), 'selectAll');
    } else {
      onBulkSelect?.([], 'deselectAll');
    }
  };

  const handleProductSelect = (productId, checked) => {
    if (checked) {
      onBulkSelect?.([productId], 'select');
    } else {
      onBulkSelect?.([productId], 'deselect');
    }
  };

  // (fin: getValidationStatus reubicado)

  const truncateText = (text, maxLength = 40) => {
    if (!text) return '';
    return text?.length > maxLength ? text?.substring(0, maxLength) + '...' : text;
  };

  // Enhanced error analysis function
  const analyzeErrors = () => {
    const errorAnalysis = {
      totalProducts: products?.length,
      productsWithErrors: 0,
      productsWithWarnings: 0,
      commonErrors: {},
      commonWarnings: {},
      recommendations: []
    };

    products?.forEach(product => {
      const validation = getValidationStatus(product);
      
      if (!validation?.isValid) {
        errorAnalysis.productsWithErrors++;
      }
      
      if (validation?.hasWarnings) {
        errorAnalysis.productsWithWarnings++;
      }

      // Count common errors
      validation?.errors?.forEach(error => {
        errorAnalysis.commonErrors[error] = (errorAnalysis?.commonErrors?.[error] || 0) + 1;
      });

      // Count common warnings
      validation?.warnings?.forEach(warning => {
        errorAnalysis.commonWarnings[warning] = (errorAnalysis?.commonWarnings?.[warning] || 0) + 1;
      });
    });

    // Generate recommendations
    if (errorAnalysis?.commonErrors?.['Título requerido'] > 0) {
      errorAnalysis?.recommendations?.push('Completar títulos faltantes en productos');
    }
    if (errorAnalysis?.commonErrors?.['Precio inválido'] > 0) {
      errorAnalysis?.recommendations?.push('Revisar y corregir precios inválidos');
    }
    if (errorAnalysis?.commonWarnings?.['Categoría requerida'] > 5) {
      errorAnalysis?.recommendations?.push('Asignar categorías a productos sin clasificar');
    }
    if (errorAnalysis?.commonWarnings?.['Sin imágenes válidas'] > 10) {
      errorAnalysis?.recommendations?.push('Verificar enlaces de imágenes');
    }

    return errorAnalysis;
  };

  const handleErrorAnalysis = () => {
    const analysis = analyzeErrors();
    setShowErrorAnalysis(true);
    onErrorAnalysis?.(analysis);
  };

  // Check if all filtered products are selected (for proper select all state)
  const allSelected = sortedProducts?.length > 0 && 
    sortedProducts?.every(product => selectedProducts?.includes(product?.id));
  
  const someSelected = selectedProducts?.length > 0 && !allSelected;

  const renderCellContent = (product, column) => {
    const value = product?.[column?.key];
    const renderUrl = (url) => {
      if (!url || typeof url !== 'string') return <span className="text-muted-foreground text-sm italic">-</span>;
      const isValid = /^https?:\/\//i.test(url);
      return (
        <div className="space-y-1">
          <div className="flex items-center space-x-2">
            <Icon name={isValid ? 'ExternalLink' : 'AlertTriangle'} size={14} className={isValid ? 'text-success' : 'text-warning'} />
            <span className={`text-xs font-medium ${isValid ? 'text-success' : 'text-warning'}`}>
              {isValid ? 'Enlace válido' : 'Enlace inválido'}
            </span>
          </div>
          <div className="text-xs max-w-xs">
            {isValid ? (
              <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate block" onClick={(e)=>e.stopPropagation()}>
                {truncateText(url, 60)}
              </a>
            ) : (
              <span className="text-text-secondary">{truncateText(url, 60)}</span>
            )}
          </div>
        </div>
      );
    };
    
    switch (column?.key) {
      case 'titulo': case'title':
        const title = product?.titulo || product?.title || '';
        const truncatedTitle = truncateText(title, 50);
        const isTitleTruncated = title && title?.length > 50;
        return (
          <div className="space-y-1">
            <p className="font-medium text-text-primary" title={title}>
              {truncatedTitle}
            </p>
            {isTitleTruncated && (
              <div className="text-xs text-warning">
                <Icon name="AlertTriangle" size={12} className="inline mr-1" />
                Título truncado
              </div>
            )}
          </div>
        );
      
      case 'precio': case'price':
        const price = product?.precio || product?.price || 0;
        return (
          <span className="font-medium text-text-primary">
            ${price?.toLocaleString('es-AR') || '0'}
          </span>
        );
      
      case 'stock':
        return (
          <span className={`text-sm ${value > 0 ? 'text-success' : 'text-text-secondary'}`}>
            {value || 0}
          </span>
        );
      
      case 'categoria': case'category':
        const category = product?.categoria || product?.category || '';
        return (
          <span className="text-text-secondary text-sm">
            {category || 'Sin categoría'}
          </span>
        );

      case 'descripcion': case'description':
        const description = product?.descripcion || product?.description || '';
        return (
          <span className="text-text-secondary text-sm" title={description}>
            {truncateText(description, 60)}
          </span>
        );

      // FIXED: Handle image columns properly to display links
      case 'imagen1': case 'imagen2': case 'imagen3': case 'imagen4': case 'imagen5':
        const imageUrl = value || '';
        if (!imageUrl) {
          return (
            <div className="flex items-center space-x-2">
              <Icon name="ImageOff" size={14} className="text-muted-foreground" />
              <span className="text-muted-foreground text-sm italic">Sin enlace</span>
            </div>
          );
        }
        
        // Check if it's a valid URL
        const isValidUrl = imageUrl?.startsWith('http');
        return (
          <div className="space-y-1">
            <div className="flex items-center space-x-2">
              {isValidUrl ? (
                <Icon name="ExternalLink" size={14} className="text-success" />
              ) : (
                <Icon name="AlertTriangle" size={14} className="text-warning" />
              )}
              <span className={`text-xs font-medium ${isValidUrl ? 'text-success' : 'text-warning'}`}>
                {isValidUrl ? 'Enlace válido' : 'Enlace inválido'}
              </span>
            </div>
            {/* Display the URL as clickable text (not as image) */}
            <div className="text-xs text-text-primary max-w-xs">
              {isValidUrl ? (
                <a 
                  href={imageUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-primary hover:underline truncate block text-blue-600"
                  title={`Abrir enlace: ${imageUrl}`}
                  onClick={(e) => e?.stopPropagation()}
                >
                  {truncateText(imageUrl, 50)}
                </a>
              ) : (
                <span 
                  className="cursor-help text-gray-500"
                  title={`Enlace inválido: ${imageUrl}`}
                >
                  {truncateText(imageUrl, 50)}
                </span>
              )}
            </div>
            {/* Copy to clipboard button */}
            <button
              onClick={(e) => {
                e?.stopPropagation();
                navigator.clipboard?.writeText(imageUrl);
              }}
              className="text-xs text-accent hover:text-accent-foreground flex items-center space-x-1 opacity-70 hover:opacity-100"
              title="Copiar enlace"
            >
              <Icon name="Copy" size={12} />
              <span>Copiar URL</span>
            </button>
          </div>
        );

      // URL columns (Apify JSON)
      case 'URL':
      case 'URL de producto no normalizada':
        return renderUrl(value);

      // Apify high-res images list (comma-separated or array)
      case 'Imágenes de alta resolución':
        if (!value) return <span className="text-muted-foreground text-sm italic">Sin enlaces</span>;
        const list = Array.isArray(value) ? value : String(value).split(',').map(s=>s.trim()).filter(Boolean);
        return (
          <div className="space-y-1">
            {list.slice(0,3).map((url, idx)=> (
              <div key={idx}>{renderUrl(url)}</div>
            ))}
            {list.length > 3 && (
              <span className="text-xs text-text-secondary">… y {list.length-3} más</span>
            )}
          </div>
        );

      case 'fechaCreacion': case 'ultimaActualizacion':
        return (
          <span className="text-text-secondary text-sm">
            {value ? new Date(value)?.toLocaleDateString('es-ES') : '-'}
          </span>
        );

      case 'tituloAsignado':
        return (
          <span className="text-text-secondary text-sm" title={value}>
            {truncateText(String(value || '-'), 40)}
          </span>
        );

      default:
        if (value === null || value === undefined || value === '') {
          return <span className="text-muted-foreground text-sm italic">-</span>;
        }
        if (typeof value === 'object') {
          let json = '';
          try {
            json = JSON.stringify(value);
          } catch {
            json = String(value);
          }
          return (
            <span className="text-text-secondary text-sm" title={json}>
              {truncateText(json, 80)}
            </span>
          );
        }
        return (
          <span className="text-text-secondary text-sm" title={value}>
            {truncateText(String(value || '-'), 30)}
          </span>
        );
    }
  };

  return (
    <div className="bg-surface rounded-lg border border-border">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div>
            <h3 className="text-lg font-semibold text-text-primary">
              Vista Previa de Productos (hasta 10,000)
            </h3>
            <p className="text-text-secondary">
              {products?.length} productos encontrados • {selectedProducts?.length} seleccionados
              • {columnsToRender?.length} columnas mostradas
            </p>
          </div>

          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
            <Input
              type="search"
              placeholder="Buscar productos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="w-full sm:w-64"
            />
            <Button
              variant="outline"
              iconName="Eye"
              iconPosition="left"
              onClick={() => setShowAllColumns(!showAllColumns)}
              className={showAllColumns ? 'bg-primary/10 border-primary/30' : ''}
            >
              {showAllColumns ? 'Ocultar Columnas' : `Todas las ${allDetectedColumns?.length} Columnas`}
            </Button>
            <Button
              variant="outline"
              iconName="AlertTriangle"
              iconPosition="left"
              onClick={handleErrorAnalysis}
              className="bg-warning/10 hover:bg-warning/20 border-warning/30"
            >
              Detectar Errores
            </Button>
            <Button
              variant="outline"
              iconName="Filter"
              iconPosition="left"
              onClick={() => setShowFilters(prev => !prev)}
              aria-expanded={showFilters}
              aria-controls="filters-panel"
            >
              {activeFilterCount > 0
                ? `Filtros Excel (${activeFilterCount})`
                : 'Filtros Excel'}
            </Button>
          </div>
        </div>

        {/* Column info */}
        {showAllColumns && (
          <div className="mt-4 p-3 bg-accent/10 rounded-lg border border-accent/20">
            <div className="flex items-center space-x-2">
              <Icon name="Info" className="text-accent" size={16} />
              <span className="text-sm font-medium text-accent">
                Mostrando todas las columnas completas incluyendo los enlaces de imágenes
              </span>
            </div>
          </div>
        )}

        {/* Filtros estilo Excel */}
        {showFilters && (
          <div
            id="filters-panel"
            className="mt-4 rounded-lg border border-border bg-muted/40 p-4"
          >
            <div className="flex flex-col gap-4">

              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <div className="flex items-center gap-2 font-semibold text-text-primary">
                    <Icon
                      name="Filter"
                      size={16}
                      className="text-primary"
                    />
                    Filtros tipo Excel
                  </div>

                  <div className="mt-1 text-xs text-text-secondary">
                    Filtra por valores de cualquier columna y combina varios filtros.
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                    {activeFilterCount} activo{activeFilterCount === 1 ? '' : 's'}
                  </span>

                  {activeFilterCount > 0 && (
                    <button
                      type="button"
                      onClick={clearAllExcelFilters}
                      className="rounded-md border border-border bg-background px-3 py-2 text-xs hover:bg-muted"
                    >
                      Limpiar todos
                    </button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-2 lg:grid-cols-[1fr_auto_auto]">

                <select
                  value={filterColumn}
                  onChange={(e) => {
                    setFilterColumn(e.target.value);
                    setFilterSearch('');
                  }}
                  className="rounded-md border border-border bg-background px-3 py-2 text-sm text-text-primary"
                >
                  <option value="">
                    Seleccionar columna...
                  </option>

                  {columnsToRender.map(column => (
                    <option
                      key={column.key}
                      value={column.key}
                    >
                      {column.label}
                    </option>
                  ))}
                </select>

                <button
                  type="button"
                  disabled={!filterColumn}
                  onClick={() =>
                    setSortConfig({
                      key: filterColumn,
                      direction: 'asc'
                    })
                  }
                  className="rounded-md border border-border bg-background px-3 py-2 text-sm hover:bg-muted disabled:opacity-40"
                >
                  A → Z
                </button>

                <button
                  type="button"
                  disabled={!filterColumn}
                  onClick={() =>
                    setSortConfig({
                      key: filterColumn,
                      direction: 'desc'
                    })
                  }
                  className="rounded-md border border-border bg-background px-3 py-2 text-sm hover:bg-muted disabled:opacity-40"
                >
                  Z → A
                </button>

              </div>

              {filterColumn && (
                <div className="rounded-lg border border-border bg-background p-3">

                  <div className="relative">
                    <Icon
                      name="Search"
                      size={14}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary"
                    />

                    <input
                      type="search"
                      value={filterSearch}
                      onChange={(e) =>
                        setFilterSearch(e.target.value)
                      }
                      placeholder="Buscar valores..."
                      className="w-full rounded-md border border-border bg-surface py-2 pl-9 pr-3 text-sm"
                    />
                  </div>

                  <div className="mt-3 flex gap-4">
                    <button
                      type="button"
                      onClick={selectAllExcelValues}
                      className="text-xs font-semibold text-primary hover:underline"
                    >
                      Seleccionar todo
                    </button>

                    <button
                      type="button"
                      onClick={clearAllExcelValues}
                      className="text-xs text-text-secondary hover:text-text-primary"
                    >
                      Quitar selección
                    </button>

                    {excelFilters?.[filterColumn] && (
                      <button
                        type="button"
                        onClick={() =>
                          clearColumnExcelFilter(filterColumn)
                        }
                        className="ml-auto text-xs font-semibold text-error hover:underline"
                      >
                        Limpiar filtro
                      </button>
                    )}
                  </div>

                  <div className="mt-3 max-h-64 overflow-y-auto rounded-md border border-border">

                    {visibleFilterValues.length > 0 ? (
                      visibleFilterValues.map(value => {
                        const checked =
                          !Array.isArray(selectedColumnValues) ||
                          selectedColumnValues.includes(value);

                        return (
                          <label
                            key={value}
                            className="flex cursor-pointer gap-2 border-b border-border/50 px-3 py-2 last:border-0 hover:bg-muted/50"
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() =>
                                toggleExcelValue(value)
                              }
                            />

                            <span
                              className="break-all text-sm text-text-primary"
                              title={displayExcelValue(value)}
                            >
                              {displayExcelValue(value)}
                            </span>
                          </label>
                        );
                      })
                    ) : (
                      <div className="p-5 text-center text-sm text-text-secondary">
                        No hay valores coincidentes.
                      </div>
                    )}

                  </div>
                </div>
              )}

              {activeFilterCount > 0 && (
                <div className="flex flex-wrap gap-2">

                  {Object.keys(excelFilters).map(columnKey => {
                    const col =
                      columnsToRender.find(
                        column =>
                          column.key === columnKey
                      );

                    return (
                      <button
                        key={columnKey}
                        type="button"
                        onClick={() =>
                          clearColumnExcelFilter(columnKey)
                        }
                        className="flex items-center gap-1 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-medium text-primary"
                      >
                        {col?.label || columnKey}

                        <Icon
                          name="X"
                          size={12}
                        />
                      </button>
                    );
                  })}

                </div>
              )}

            </div>
          </div>
        )}
      </div>

      {/* Error Analysis Panel */}
      {showErrorAnalysis && (
        <div className="p-4 border-b border-border bg-warning/5 dark:bg-warning/15">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium text-text-primary">Análisis de Errores</h4>
            <Button
              variant="ghost"
              size="sm"
              iconName="X"
              onClick={() => setShowErrorAnalysis(false)}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {(() => {
              const analysis = analyzeErrors();
              return (
                <>
                  <div className="p-3 bg-error/10 dark:bg-error/20 rounded-lg border border-error/20 dark:border-error/40">
                    <div className="text-lg font-bold text-error">{analysis?.productsWithErrors}</div>
                    <div className="text-sm text-text-secondary">Productos con errores</div>
                  </div>
                  <div className="p-3 bg-warning/10 dark:bg-warning/20 rounded-lg border border-warning/20 dark:border-warning/40">
                    <div className="text-lg font-bold text-warning">{analysis?.productsWithWarnings}</div>
                    <div className="text-sm text-text-secondary">Productos con advertencias</div>
                  </div>
                  <div className="p-3 bg-success/10 dark:bg-success/20 rounded-lg border border-success/20 dark:border-success/40">
                    <div className="text-lg font-bold text-success">
                      {analysis?.totalProducts - analysis?.productsWithErrors}
                    </div>
                    <div className="text-sm text-text-secondary">Productos válidos</div>
                  </div>
                </>
              );
            })()}
          </div>
          <div className="mt-3">
            <h5 className="text-sm font-medium text-text-primary mb-2">Recomendaciones:</h5>
            <ul className="text-sm text-text-secondary space-y-1">
              {analyzeErrors()?.recommendations?.map((rec, index) => (
                <li key={index} className="flex items-center space-x-2">
                  <Icon name="CheckCircle" size={12} className="text-primary" />
                  <span>{rec}</span>
                </li>
              ))}
              {analyzeErrors()?.recommendations?.length === 0 && (
                <li className="text-success">¡No se encontraron problemas críticos!</li>
              )}
            </ul>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
        <table className="w-full">
          <thead className="bg-muted sticky top-0 z-20">
            <tr>
              <th className="w-12 p-4 sticky left-0 bg-muted z-30 border-r border-border">
                <Checkbox
                  checked={allSelected}
                  onChange={(e) => handleSelectAll(e?.target?.checked)}
                  indeterminate={someSelected}
                  aria-label={`Seleccionar todos los productos (${sortedProducts?.length})`}
                />
              </th>
              {columnsToRender?.map((column) => (
                <th
                  key={column?.key}
                  className={`text-left p-4 font-medium text-text-primary ${
                    column?.width || 'min-w-32'
                  } ${
                    column?.isImageLink ? 'min-w-80' : ''
                  }`}
                >
                  <div className="flex items-center gap-1">

                    <button
                      type="button"
                      onClick={() =>
                        handleSort(column?.key)
                      }
                      className="flex min-w-0 items-center gap-1 hover:text-primary"
                      title="Ordenar"
                    >
                      <span className="truncate">
                        {column?.label}
                      </span>

                      <Icon
                        name="ArrowUpDown"
                        size={12}
                        className={
                          sortConfig?.key === column?.key
                            ? 'text-primary'
                            : 'text-text-secondary'
                        }
                      />

                      {column?.isImageLink && (
                        <Icon
                          name="Image"
                          size={13}
                          className="text-accent"
                        />
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();

                        setFilterColumn(column?.key);
                        setFilterSearch('');
                        setShowFilters(true);
                      }}
                      title={`Filtrar ${column?.label}`}
                      className={`ml-auto flex h-6 w-6 shrink-0 items-center justify-center rounded ${
                        excelFilters?.[column?.key]
                          ? 'bg-primary text-primary-foreground'
                          : 'text-text-secondary hover:bg-background hover:text-primary'
                      }`}
                    >
                      <Icon
                        name="Filter"
                        size={12}
                      />
                    </button>

                  </div>
                </th>
              ))}
              <th className="text-left p-4 font-medium text-text-primary sticky right-12 bg-muted z-20 border-l border-border">Estado</th>
              <th className="w-24 p-4 font-medium text-text-primary sticky right-0 bg-muted z-30 border-l border-border">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {paginatedProducts?.map((product, index) => {
              const validation = getValidationStatus(product);
              const isSelected = selectedProducts?.includes(product?.id);

              return (
                <tr key={product?.id} className="border-b border-border hover:bg-muted/30">
                  <td className="p-4 sticky left-0 bg-surface z-10 border-r border-border">
                    <Checkbox
                      checked={isSelected}
                      onChange={(e) => handleProductSelect(product?.id, e?.target?.checked)}
                      aria-label={`Seleccionar producto ${product?.titulo || product?.title}`}
                    />
                  </td>
                  {columnsToRender?.map((column) => (
                    <td key={column?.key} className={`p-4 ${column?.width || 'min-w-32'} ${column?.isImageLink ? 'min-w-80' : ''}`}>
                      {renderCellContent(product, column)}
                    </td>
                  ))}
                  <td className="p-4 sticky right-12 bg-surface z-10 border-l border-border">
                    <div className="flex items-center space-x-2">
                      {validation?.isValid && !validation?.hasWarnings ? (
                        <div className="flex items-center space-x-1 text-success">
                          <Icon name="CheckCircle" size={16} />
                          <span className="text-sm">Válido</span>
                        </div>
                      ) : validation?.isValid && validation?.hasWarnings ? (
                        <div className="flex items-center space-x-1 text-warning">
                          <Icon name="AlertTriangle" size={16} />
                          <span className="text-sm">{validation?.warnings?.length} advertencias</span>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-1 text-error">
                          <Icon name="AlertCircle" size={16} />
                          <span className="text-sm">{validation?.errors?.length} errores</span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="p-4 sticky right-0 bg-surface z-30 border-l border-border">
                    <div className="flex items-center space-x-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Edit"
                        onClick={() => onProductUpdate(product?.id)}
                        aria-label={`Editar producto ${product?.titulo || product?.title}`}
                        title="Editar"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Trash2"
                        onClick={() => onProductDelete?.(product?.id)}
                        aria-label={`Eliminar producto ${product?.titulo || product?.title}`}
                        title="Eliminar"
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

          {/* Enhanced Pagination for 10,000 products */}
      {totalPages > 1 && (
        <div className="p-4 border-t border-border">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div className="text-sm text-text-secondary">
              Mostrando {((currentPage - 1) * itemsPerPage) + 1} a {Math.min(currentPage * itemsPerPage, sortedProducts?.length)} de {sortedProducts?.length} productos
              {selectedProducts?.length > 0 && (
                <span className="ml-2 text-primary font-medium">
                  • {selectedProducts?.length} seleccionados en total
                </span>
              )}
            </div>
            
            <div className="flex items-center justify-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                iconName="ChevronsLeft"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(1)}
                title="Primera página"
              />
              <Button
                variant="outline"
                size="sm"
                iconName="ChevronLeft"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              >
                Anterior
              </Button>
              
              {/* Page number display for large datasets */}
              <div className="flex items-center space-x-1">
                <span className="text-sm text-text-secondary">
                  Página {currentPage} de {totalPages}
                </span>
                {totalPages > 10 && (
                  <div className="ml-2">
                    <Input
                      type="number"
                      min="1"
                      max={totalPages}
                      value={currentPage}
                      onChange={(e) => {
                        const page = parseInt(e?.target?.value);
                        if (page >= 1 && page <= totalPages) {
                          setCurrentPage(page);
                        }
                      }}
                      className="w-20 h-8 text-center"
                      placeholder="Ir a"
                    />
                  </div>
                )}
              </div>
              
              <Button
                variant="outline"
                size="sm"
                iconName="ChevronRight"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              >
                Siguiente
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="ChevronsRight"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(totalPages)}
                title="Última página"
              />
            </div>
          </div>
          
          {/* Capacity info */}
          <div className="mt-3 text-center">
            <span className="text-xs text-text-secondary bg-muted px-3 py-1 rounded-full">
              Capacidad optimizada para hasta 10,000 productos • Paginación: {itemsPerPage} por página
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductPreviewTable;